import type { ServiceCategoryFilter } from "@/components/pages/analytics/front-desk/components/services/mockData";
import type { ServicesListResponse } from "@/types/analytics-services";
import type { TenantServicesTableRow } from "@/types/analytics-tenant";

export type AdminServicePreview = {
  id: string;
  name: string;
  imageUrl?: string;
  category: Exclude<ServiceCategoryFilter, "all">;
};

export type AdminHotelServicesRow = {
  id: string;
  hotelBrand: string;
  topServices: AdminServicePreview[];
};

const API_CATEGORY_TO_FILTER: Record<
  string,
  Exclude<ServiceCategoryFilter, "all">
> = {
  GUEST_SERVICES: "guestServices",
  ACTIVITIES: "activities",
  EXTERNAL_SERVICES: "externalServices",
  GUESTERIA_PARTNERS: "guesteriaPartners",
};

function resolveServiceImageUrl(photoId: string | null): string | undefined {
  if (!photoId?.trim()) {
    return undefined;
  }

  if (/^https?:\/\//i.test(photoId)) {
    return photoId;
  }

  return undefined;
}

function mapApiCategoryToFilter(
  apiCategory: string | undefined,
  fallback: ServiceCategoryFilter,
): Exclude<ServiceCategoryFilter, "all"> {
  if (apiCategory && apiCategory in API_CATEGORY_TO_FILTER) {
    return API_CATEGORY_TO_FILTER[apiCategory];
  }

  return fallback === "all" ? "guestServices" : fallback;
}

export function mapServicesListToPreviews(
  response: ServicesListResponse | null | undefined,
  selectedCategory: ServiceCategoryFilter,
): AdminServicePreview[] {
  if (!response) {
    return [];
  }

  return response.data.map((row) => ({
    // A Guesteria Partners booking made outside the catalog has no service
    // id; this preview cell doesn't link anywhere, so an empty id is safe.
    id: row.service.id ?? "",
    name: row.service.name,
    imageUrl: resolveServiceImageUrl(row.service.photoId),
    category: mapApiCategoryToFilter(response.category, selectedCategory),
  }));
}

export function mapTenantServicesToHotelRows(
  hotels: TenantServicesTableRow[],
  listsByHotelId: Map<string, ServicesListResponse | null>,
  category: ServiceCategoryFilter,
  maxTopServices = 5,
): AdminHotelServicesRow[] {
  return hotels.map((hotel) => ({
    id: hotel.hotelId,
    hotelBrand: hotel.hotelName,
    topServices: mapServicesListToPreviews(
      listsByHotelId.get(hotel.hotelId),
      category,
    ).slice(0, maxTopServices),
  }));
}
